import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, filter, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ConfigurationService } from './configuration.service';
import { TestUserCatalogService } from './test-user-catalog.service';
import { UserAccountService } from './user-account.service';

@Injectable({
  providedIn: 'root',
})
// RolesGuard is a custom guard that checks if the user has the required roles to access a route
export class RolesGuard {
  constructor(
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    public accountService: UserAccountService,
    private configService: ConfigurationService,
    private router: Router,
    private testUserCatalog: TestUserCatalogService,
  ) {}

  // canActivate is a function that checks if the user has the required roles to access a route
  canActivate: CanActivateFn = (
    next: ActivatedRouteSnapshot,
    _state: RouterStateSnapshot,
  ): Observable<boolean | UrlTree> => {
    // inProgress$ is an observable that emits the current state of the authentication request

    // In test mode, use a simpler approach
    if (environment.testMode) {
      return this.handleTestMode(next);
    }

    return combineLatest([
      this.msalBroadcastService.inProgress$.pipe(
        filter(
          (status: InteractionStatus) =>
            status === InteractionStatus.None || status === InteractionStatus.Startup,
        ),
      ),
      this.configService.featureFlags$.pipe(filter(featureFlags => featureFlags !== null)),
    ]).pipe(
      switchMap(([_status, currentFeatures]) => {
        console.log(currentFeatures, next.data.features);
        // account is the current user account
        const account = this.authService.instance.getAllAccounts()[0];

        // if there is no account, navigate to the noaccess route
        if (!account) {
          this.router.navigate(['/noaccess']);
          return of(false);
        }

        if (!this.accountService.getRoles() || this.accountService.getRoles().length === 0) {
          this.accountService.setUser(account);
        }
        const userRoles = this.accountService.getRoles();
        const requiredRoles: string[] = next.data.roles;

        if (requiredRoles && !(userRoles && requiredRoles.some(role => userRoles.includes(role)))) {
          console.log('RolesGuard no roles');
          this.router.navigate(['/noaccess']);
          return of(false);
        }

        // check if the user has the required features
        if (next.data.features) {
          for (const feature of next.data.features) {
            if (!(currentFeatures as any)[feature]) {
              console.log('RolesGuard no features');
              this.router.navigate(['/noaccess']);
              return of(false);
            }
          }
        }

        return of(true);
      }),
      catchError(() => {
        // if there is an error, navigate to the noaccess route
        this.router.navigate(['/noaccess']);
        return of(false);
      }),
    );
  };

  private handleTestMode(next: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
    // Check if we have a current test user from the catalog
    const currentUser = this.testUserCatalog.getCurrentUser();

    if (!currentUser) {
      // Redirect to noaccess page where user can login
      this.router.navigate(['/noaccess']);
      return of(false);
    }

    // Set the user in the account service if not already set
    if (!this.accountService.getRoles() || this.accountService.getRoles().length === 0) {
      // Create a mock account object
      const account = {
        homeAccountId: `${currentUser.oid}.6e63ffc0-c2fc-4cc4-b6c4-666b2ce89d92`,
        localAccountId: currentUser.oid,
        username: currentUser.email,
        environment: 'login.windows.net',
        tenantId: '6e63ffc0-c2fc-4cc4-b6c4-666b2ce89d92',
        name: currentUser.name,
        idTokenClaims: {
          aud: '6b79bf94-8a9e-4a78-ad23-de2e7dc13ee3',
          exp: Math.floor(Date.now() / 1000) + 3600,
          name: currentUser.name,
          oid: currentUser.oid,
          preferred_username: currentUser.email,
          roles: currentUser.roles,
          tid: '6e63ffc0-c2fc-4cc4-b6c4-666b2ce89d92',
        },
      };

      this.accountService.setUser(account);
    }

    const userRoles = this.accountService.getRoles();
    const requiredRoles: string[] = next.data.roles;

    // If no roles are required, allow access
    if (!requiredRoles || requiredRoles.length === 0) {
      return of(true);
    }

    if (requiredRoles && !(userRoles && requiredRoles.some(role => userRoles.includes(role)))) {
      this.router.navigate(['/noaccess']);
      return of(false);
    }

    // In test mode, we'll skip feature flag checks for now
    // You can add feature flag logic here if needed

    return of(true);
  }
}
